import React, { useMemo, useState, useCallback, useEffect } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { useTable, useSortBy, useFilters, useGlobalFilter } from "react-table";
import { format } from "date-fns";
import { FaExclamation } from "react-icons/fa";
import { debounce } from 'lodash';
import { FaFileAlt, FaCheck, FaTimes } from "react-icons/fa";

import Loading from "../../../components/Loading";
import {
  GlobalFilter,
  DefaultFilterForColumn,
  StatusFilter,
  DateRangeFilter,
  dateBetweenFilterFn,
  ModeFilter,
} from "./Filter";

import "../tables.scss";

const fdaLabels = {
  "REVIEW": "FDA Under PGA Review",
  "DOC REVIEW": "FDA Doc Review",
  "EXAM": "FDA Exam",
  "MAY PROCEED": "FDA May Proceed",
  "DETAINED": "Detained",
};

const Circle = ({ color }) => {
  return (
    <div
      style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: `${color}`,
        display: "inline-block",
        marginRight: "6px",
        position: "relative",
        top: "-2px",
        left: "0",
      }}
    ></div>
  );
};

/**
  1. SUBMITTED -> Awating Arrival (frontend)
  2. Pending Docs -> 5Days before ETA
  3. Pending Release
  4. Released 
*/
const statusIcon = (status) => {
  switch (status.toLowerCase()) {
    case "not cleared by afb":
      return (
        <>
          <Circle color="#FF623E" />
          <span
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            {status.toLowerCase()}
          </span>
        </>
      );
    case "released":
      return (
        <>
          <Circle color="#23B574" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            {status.toLowerCase()}
          </p>
        </>
      );
    case "bill on file":
    case "no bill match":
      return (
        <>
          <Circle color="#8FBC8F" />
          <span
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            {status.toLowerCase()}
          </span>
        </>
      );
    case "pending release":
      return (
        <>
          <Circle color="#DDDD32" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            {status.toLowerCase()}
          </p>
        </>
      );
    case "cbp intensive exam":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {"CBP Intensive Exam"}
          </p>
        </>
      );

    case "cbp vax exam":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {"CBP Vax Exam"}
          </p>
        </>
      );

    case "cbp vacis exam":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {"CBP Vacis Exam"}
          </p>
        </>
      );

    case "cbp docs review hold":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {"CBP Docs Review Hold"}
          </p>
        </>
      );
    case "cbp detained":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {"CBP Detained"}
          </p>
        </>
      );
    case "it shipment":
      return (
        <>
          <Circle color="#008B8B" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {status
              .toLowerCase()
              .split(" ")
              .map((i) => i[0].toUpperCase() + i.substring(1))
              .join(" ")
              .replace("It", "IT")}
          </p>
        </>
      );
    case "isf hold":
      return (
        <>
          <Circle color="#3C45C8" />
          <p
            style={{
              display: "inline-block",
            }}
          >
            {status
              .toLowerCase()
              .split(" ")
              .map((i) => i[0].toUpperCase() + i.substring(1))
              .join(" ")
              .replace("Cbp", "CBP")
              .replace("Isf", "ISF")}
          </p>
        </>
      );
    case "submitted":
      return (
        <>
          <Circle color="#77C4FF" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            Awaiting Arrival
          </p>
        </>
      );
    case "pending documents":
      return (
        <>
          <Circle color="#EB1B23" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            Documents Required
          </p>
        </>
      );
    case "pending review":
      return (
        <>
          <Circle color="#d31bebff" />
          <span
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            Pending Review
          </span>
        </>
      );
    case "admissible":
    case "released":
    case "accepted":
    case "liquidated":
      return (
        <>
          <Circle color="#2336B5" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            Other
          </p>
        </>
      );
    case "entry canceled":
      return (
        <>
          <Circle color="#EB1B23" />
          <p
            style={{
              textTransform: "capitalize",
              display: "inline-block",
            }}
          >
            Entry Canceled
          </p>
        </>
      );
    default:
      return <p>{status.toLowerCase()}</p>;
  }
};

//Function that recieves the number of extra containers
const fewContainers = (containers) => {
  var formatted = "";
  for (let i = 0; i < containers?.length; i++) {
    formatted += containers[i] + ", ";
  }

  return <span>{formatted.slice(0, formatted?.length - 2)}</span>;
};

const ManyContainers = ({ containers }) => {
  var firstShipments = "";
  var otherShipments = "";
  var count = 0;
  var [opened, setOpened] = useState(false);

  if (!(typeof containers === "undefined" || containers?.length <= 0)) {
    for (let i = 0; i <= 1 && i < containers.length; i++) {
      firstShipments += containers[i] + ", ";
    }
    for (let i = 2; i < containers?.length; i++) {
      otherShipments += containers[i] + ", ";
      count += 1;
    }
  }

  return (
    <span onClick={() => setOpened(!opened)} style={{ cursor: "pointer" }}>
      {firstShipments.slice(0, firstShipments?.length - 2)},
      {opened ? otherShipments.slice(0, otherShipments?.length - 2) : ""}
      {!opened ? (
        <span className="container-extensor">+{count}</span>
      ) : (
        <span className="container-extensor" style={{ marginRight: "-1rem" }}>
          close
        </span>
      )}
    </span>
  );
};

const fewMasterBillLading = (master_bill_lading) => {
  var formatted = "";
  for (let i = 0; i < master_bill_lading?.length; i++) {
    formatted += master_bill_lading[i] + ", ";
  }

  return <span>{formatted.slice(0, formatted?.length - 2)}</span>;
};

const ManyMasterBillLading = ({ master_bill_lading }) => {
  var firstShipments = "";
  var otherShipments = "";
  var count = 0;
  var [opened, setOpened] = useState(false);

  if (!(typeof master_bill_lading === "undefined" || master_bill_lading?.length <= 0)) {
    for (let i = 0; i <= 1 && i < master_bill_lading.length; i++) {
      firstShipments += master_bill_lading[i] + ", ";
    }
    for (let i = 2; i < master_bill_lading?.length; i++) {
      otherShipments += master_bill_lading[i] + ", ";
      count += 1;
    }
  }

  return (
    <span onClick={() => setOpened(!opened)} style={{ cursor: "pointer" }}>
      {firstShipments.slice(0, firstShipments?.length - 2)},
      {opened ? otherShipments.slice(0, otherShipments?.length - 2) : ""}
      {!opened ? (
        <span className="container-extensor">+{count}</span>
      ) : (
        <span className="container-extensor" style={{ marginRight: "-1rem" }}>
          close
        </span>
      )}
    </span>
  );
};

const fewHouseBillLading = (house_bill_lading) => {
  var formatted = "";
  for (let i = 0; i < house_bill_lading?.length; i++) {
    formatted += house_bill_lading[i] + ", ";
  }

  return <span>{formatted.slice(0, formatted?.length - 2)}</span>;
};

const ManyHouseBillLading = ({ house_bill_lading }) => {
  var firstShipments = "";
  var otherShipments = "";
  var count = 0;
  var [opened, setOpened] = useState(false);

  if (!(typeof house_bill_lading === "undefined" || house_bill_lading?.length <= 0)) {
    for (let i = 0; i <= 1 && i < house_bill_lading.length; i++) {
      firstShipments += house_bill_lading[i] + ", ";
    }
    for (let i = 2; i < house_bill_lading?.length; i++) {
      otherShipments += house_bill_lading[i] + ", ";
      count += 1;
    }
  }

  return (
    <span onClick={() => setOpened(!opened)} style={{ cursor: "pointer" }}>
      {firstShipments.slice(0, firstShipments?.length - 2)},
      {opened ? otherShipments.slice(0, otherShipments?.length - 2) : ""}
      {!opened ? (
        <span className="container-extensor">+{count}</span>
      ) : (
        <span className="container-extensor" style={{ marginRight: "-1rem" }}>
          close
        </span>
      )}
    </span>
  );
};

const COLUMNS = [
  {
    Header: "Entry Number",
    accessor: "file_number",
    Cell: ({ value, row }) => {
      const { account_no, documents_submitted: docs = [] } = row.original;
      //console.log("EntryNumber Cell → account_no:", account_no, "docs:", docs);

      const isSOUSEA = ["SOUSEA", "OCALAG"].includes(account_no);
      const hasUnsubmitted = docs.some((d) => !d.submitted);

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <p style={{ fontWeight: "bold", fontSize: "1rem", margin: 0 }}>
            {value && String(value).trim() ? value : "New Shipment"}
          </p>
          {!isSOUSEA && docs.length > 0 && (
            <div className="tooltip-container">
              <FaFileAlt
                className="tooltip-icon"
                style={{ color: hasUnsubmitted ? "red" : "green" }}
              />
              {hasUnsubmitted && (
                <div className="tooltip">
                  Missing:
                  <ul style={{ paddingLeft: "1rem", margin: 0 }}>
                    {docs
                      .filter((doc) => !doc.submitted)
                      .map((doc, i) => <li key={i}>{doc.type}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    Header: "Account no",
    accessor: "account_no",
    Cell: ({ value }) => (
      <p
        style={{
          fontWeight: "bold",
          fontSize: "1rem",
        }}
      >
        {value}
      </p>
    ),
  },
  {
    Header: "Master Bill of Lading",
    accessor: "master_bill_lading",
    Cell: ({ value }) => {
      return (
        <p style={{ fontWeight: "bold", fontSize: "1rem", margin: 0 }}>
          {value?.length > 0 ? (
            value?.length <= 2 ? (
              fewMasterBillLading(value)
            ) : (
              <ManyMasterBillLading master_bill_lading={value} />
            )
          ) : null}
        </p>
      );
    },
  },
  {
    Header: "House Bill of Lading",
    accessor: "house_bill_lading",
    Cell: ({ value }) => {
      return (
        <p style={{ fontWeight: "bold", fontSize: "1rem", margin: 0 }}>
          {value?.length > 0 ? (
            value?.length <= 2 ? (
              fewHouseBillLading(value)
            ) : (
              <ManyHouseBillLading house_bill_lading={value} />
            )
          ) : null}
        </p>
      );
    },
  },
  {
    Header: "Container Number(s)",
    accessor: "containers",
    Cell: ({ value }) => {
      return (
        <p style={{ fontWeight: "bold", fontSize: "1rem", margin: 0 }}>
          {value?.length > 0 ? (
            value?.length <= 2 ? (
              fewContainers(value)
            ) : (
              <ManyContainers containers={value} />
            )
          ) : (
            <span>No containers info provided.</span>
          )}
        </p>
      );
    },
  },
  {
    Header: "Reference Number",
    accessor: "customer_reference_no",
    Cell: ({ value }) => (
      <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
        {value}
      </span>
    ),
  },
  {
    Header: "ETA",
    accessor: "est_time_arrive",
    Cell: ({ value }) => {
      if (value !== "0000-00-00 00:00:00") {
        return (
          <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
            {format(new Date(value), "MM/dd/yyyy")}
          </span>
        );
      } else {
        return <span style={{ fontWeight: "bold", fontSize: "1rem" }}></span>;
      }
    },
    id: "date",
    filter: "dateBetween",
  },
  {
    Header: "FDA",
    accessor: "fda_review",
    Cell: ({ value }) => (
      <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
        {value}
      </span>
    ),
  },
  {
    Header: "Status",
    accessor: "status",
    Cell: ({ value, row }) => {
      const { account_no, documents_submitted: docs = [] } = row.original;
      const isSOUSEA = ["SOUSEA", "OCALAG"].includes(account_no);

      // 1) your normal status bullet/text
      const statusEl = (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontWeight: "bold",
            fontSize: "1rem",
          }}
        >
          {statusIcon(value)}
        </span>
      );

      // 2) wrap everything in a flex-col that centers cross-axis (horizontal)
      if (isSOUSEA && docs.length > 0) {
        const abbrMap = {
          "Arrival Notice": "AN",
          "Arrival Notice / Air Waybill": "AWB",
          "Bill of Lading": "BL",
          "Commercial Invoice": "CI",
          "Packing Slip": "PS",
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              rowGap: "4px",
            }}
          >
            {statusEl}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {docs.map((doc) => {
                const abbr = abbrMap[doc.type] || doc.type;
                return (
                  <div
                    key={abbr}
                    style={{ display: "flex", alignItems: "center", gap: "2px" }}
                  >
                    <span style={{ fontWeight: "bold" }}>{abbr}</span>
                    {doc.submitted ? (
                      <FaCheck color="green" size={14} />
                    ) : (
                      <FaTimes color="red" size={14} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // 3) non-SOUSEA (or no docs)
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          {statusEl}
        </div>
      );
    },
  },
];

// Simple Table
export const ShipmentsTable = ({ shipments }) => {
  // Read the highlighted ID immediately so it survives re-renders while data loads
  const [highlightedId, setHighlightedId] = useState(() => {
    const savedId = sessionStorage.getItem("lastClickedShipmentId");
    if (savedId !== null) {
      sessionStorage.removeItem("lastClickedShipmentId");
      return parseInt(savedId);
    }
    return null;
  });
  const [dataReady, setDataReady] = useState(false);

  const columns = useMemo(() => COLUMNS, []);
  const data = useMemo(() => shipments, [shipments]);

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data.hasOwnProperty("result") ? data?.result : data,
      initialState: {
        sortBy: [{ id: "date", desc: true }],
      },
    },
    useSortBy
  );

  useEffect(() => {
    const delay = setTimeout(() => {
      if (data?.hasOwnProperty("result")) {
        setDataReady(true);
      }
    }, 50); // Small debounce to prevent flicker

    return () => clearTimeout(delay);
  }, [data]);

  // Restore scroll position once data has loaded
  const scrollRestoredRef = React.useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current || !dataReady) return;
    const savedScroll = sessionStorage.getItem("shipmentsScroll");

    if (savedScroll !== null) {
      window.scrollTo(0, parseInt(savedScroll));
      sessionStorage.removeItem("shipmentsScroll");
    }
    scrollRestoredRef.current = true;
  }, [dataReady]);

  useEffect(() => {
    if (highlightedId !== null) {
      const timeout = setTimeout(() => setHighlightedId(null), 3000);
      return () => clearTimeout(timeout);
    }
  }, [highlightedId]);

  if (!dataReady) {
    return <Loading />;
  }

  return (
    <>
      <h1 style={{ marginBottom: "12.5px", fontSize: "1.3rem", color: "#303030" }}>
        Shipments
      </h1>

      <div className="table-wrapper">
        <table {...getTableBodyProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} style={{ textAlign: "left" }}>
                {headerGroup.headers.map((column) => {
                  return (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={{ textAlign: "left", cursor: "pointer" }}
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <span>▼</span>
                          ) : (
                            <span>▲</span>
                          )
                        ) : (
                          ""
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const rowId = row.original?.id;

              return (
                <tr
                  {...row.getRowProps()}
                  className={`table-row ${highlightedId === rowId ? "recently-viewed" : ""}`}
                  style={{ textAlign: "left" }}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} style={{ textAlign: "left" }}>
                        {(() => {
                          switch (cell.column.Header) {
                            case "Entry Number":
                              return (
                                <NavLink
                                  to={`/shipments/${rowId}`}
                                  onClick={() => {
                                    sessionStorage.setItem("shipmentsScroll", window.scrollY);
                                    sessionStorage.setItem("lastClickedShipmentId", rowId);
                                  }}
                                >
                                  {cell.render("Cell")}
                                </NavLink>
                              );
                            case "FDA":
                              return cell.value ? (
                                <div className="tooltip-container" style={{ margin: 0 }}>
                                  <FaExclamation
                                    className={cell.value === "MAY PROCEED" ? "pass-fda" : "fail-fda"}
                                  />
                                  <div className="tooltip">
                                    {fdaLabels[String(cell.value).toUpperCase()] || cell.value}
                                  </div>
                                </div>
                              ) : (
                                ""
                              );
                            case "House Bill of Lading":
                              return (
                                <p>
                                  {cell?.row?.original?.house_bill_lading?.join(", ")}
                                </p>
                              );
                            default:
                              return cell.render("Cell");
                          }
                        })()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const renderBillString = (input_array) => {
  let result = "";

  if (input_array?.length > 1) {
    result = `${input_array[0]} + ${input_array?.length - 1}`;
  } else if (input_array?.length == 1) {
    result = `${input_array[0]}`;
  } else {
    result = "";
  }

  return result;
};

export const ShipmentsTableSearch = ({
  shipments,
  filterState,
  setFilterState,
  statusOptions,
  onDownloadCsv,
  exportingCsv,
  onCreateShipment,
}) => {
  //console.log("incoming shipments payload:", shipments);

  const columns = useMemo(() => COLUMNS, []);
  const data = useMemo(() => shipments.data ?? shipments.result ?? [], [shipments]);

  //console.log("using rows:", data);

  const sortedShipmentIds = useMemo(() => {
    return data
      .slice() // clone to avoid in-place sort
      .sort((a, b) => new Date(a.est_time_arrive) - new Date(b.est_time_arrive))
      .map((s) => s.id);
  }, [data]);

  const filterTypes = useMemo(() => ({ dateBetween: dateBetweenFilterFn }), []);


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    setFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultFilterForColumn },
      filterTypes,
      initialState: {
        sortBy: [{ id: "date", desc: true }],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  // Read the highlighted ID immediately so it survives re-renders while data loads
  const [highlightedId, setHighlightedId] = useState(() => {
    const savedId = sessionStorage.getItem("lastClickedShipmentId");
    if (savedId !== null) {
      sessionStorage.removeItem("lastClickedShipmentId");
      return parseInt(savedId, 10);
    }
    return null;
  });
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFiltersReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!filtersReady) return;
    if (filterState.date && filterState.date.length === 2) {
      setFilter("date", filterState.date);
    } else {
      setFilter("date", undefined);
    }
  }, [filterState.date, filtersReady, setFilter]);

  // Restore scroll position once data has loaded
  const scrollRestoredRefSearch = React.useRef(false);
  useEffect(() => {
    if (scrollRestoredRefSearch.current || !filtersReady || rows.length === 0) return;

    const savedScroll = sessionStorage.getItem("shipmentsScroll");

    if (savedScroll !== null) {
      window.scrollTo(0, parseInt(savedScroll, 10));
      sessionStorage.removeItem("shipmentsScroll");
    }
    scrollRestoredRefSearch.current = true;
  }, [filtersReady, rows]);

  useEffect(() => {
    if (highlightedId === null) return;
    const timeout = setTimeout(() => setHighlightedId(null), 3000);
    return () => clearTimeout(timeout);
  }, [highlightedId]);

  if (!filtersReady) return <LoadingTable />;

  return (
    <>
      <div className="filter-wrapper">
        <GlobalFilter
          value={filterState.global}
          onChange={(v) => setFilterState((p) => ({ ...p, global: v }))}
        />

        <StatusFilter
          statusValue={filterState.status}
          accountValue={filterState.account}
          onStatusChange={(v) => setFilterState((p) => ({ ...p, status: v }))}
          onAccountChange={(v) => setFilterState((p) => ({ ...p, account: v }))}
          placeholder="Shipments filter"
          accounts={shipments.accounts}
          statusOptions={statusOptions}
        />

        <ModeFilter
            value={filterState.mode}
            onChange={(v) =>
              setFilterState((p) => ({
                ...p,
                mode: v,
              }))
            }
          />

        <DateRangeFilter
          value={filterState.date}
          onChange={(range) => {
            setFilterState((p) => ({
              ...p,
              date: range,
              dateOption: null,
            }));
          }}
        />

        {onDownloadCsv && (
          <button
            className="download-shipments-btn"
            onClick={onDownloadCsv}
            disabled={exportingCsv}
            title="Download all shipments matching current filters"
            type="button"
          >
            {exportingCsv ? "Exporting..." : "Download Shipments"}
          </button>
        )}

        {onCreateShipment && (
          <button
            id="new-user-button"
            className="add-shipment-btn"
            onClick={onCreateShipment}
            type="button"
          >
            Create Shipment
          </button>
        )}

      </div>

      <div className="table-wrapper">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} style={{ textAlign: "left" }}>
                {hg.headers.map((col) => {
                  return (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      style={{ textAlign: "left", cursor: "pointer" }}
                    >
                      {col.render("Header")}
                      <span>
                        {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : ""}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const rowId = row.original.id;
              return (
                <tr
                  {...row.getRowProps()}
                  className={
                    "table-row" + (highlightedId === rowId ? " recently-viewed" : "")
                  }
                  style={{ textAlign: "left" }}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} style={{ textAlign: "left" }}>
                        {cell.column.Header === "Entry Number" ? (
                          <NavLink
                            to={`/shipments/${rowId}`}
                            state={{
                              filteredShipmentIds: sortedShipmentIds,
                              filter: filterState,
                            }}
                            onClick={() => {
                              sessionStorage.setItem("shipmentsScroll", window.scrollY);
                              sessionStorage.setItem("lastClickedShipmentId", rowId);
                            }}
                          >
                            {cell.render("Cell")}
                          </NavLink>
                        ) : cell.column.Header === "FDA" ? (
                          cell.value ? (
                            <div className="tooltip-container" style={{ margin: 0 }}>
                              <FaExclamation
                                className={cell.value === "MAY PROCEED" ? "pass-fda" : "fail-fda"}
                              />
                              <div className="tooltip">
                                {fdaLabels[String(cell.value).toUpperCase()] || cell.value}
                              </div>
                            </div>
                          ) : null
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const LoadingTable = () => {
  return <div className="loading-table"></div>;
};
